import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import TeacherDialog from "./TeacherDialog";

function validateTeacher(dialog) {
  if (!dialog.name.trim() || !dialog.email.trim()) return "Nom et email requis";
  if (dialog.mode === "create" && !dialog.password) return "Mot de passe requis";
  return null;
}

async function submitTeacher(dialog) {
  if (dialog.mode === "create") {
    await api.post("/admin/teachers", { name: dialog.name, email: dialog.email, password: dialog.password, role: dialog.role });
    return "Professeur créé";
  }
  const payload = { name: dialog.name, email: dialog.email, role: dialog.role };
  if (dialog.password) payload.password = dialog.password;
  await api.put(`/admin/teachers/${dialog.id}`, payload);
  return "Professeur modifié";
}

function useTeachersCrud() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/teachers");
    setTeachers(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    const error = validateTeacher(dialog);
    if (error) {
      toast.error(error);
      return;
    }
    setBusy(true);
    try {
      toast.success(await submitTeacher(dialog));
      setDialog(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/teachers/${id}`);
      toast.success("Professeur supprimé");
      setConfirmId(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return { teachers, loading, dialog, setDialog, confirmId, setConfirmId, busy, save, remove };
}

function TeacherRow({ t, confirmDelete, onEdit, onAskDelete, onConfirmDelete }) {
  return (
    <div className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`teacher-row-${t.id}`}>
      <div>
        <div className="font-medium flex items-center gap-2">
          {t.name}
          <span
            className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${
              t.role === "assoc_admin" ? "bg-[var(--maroon)]/10 text-[var(--maroon)]" : "bg-[var(--parchment)] text-[var(--ink-soft)]"
            }`}
            data-testid={`teacher-role-badge-${t.id}`}
          >
            {t.role === "assoc_admin" ? "Prof. admin associé" : "Professeur"}
          </span>
        </div>
        <div className="text-xs text-[var(--ink-soft)] font-mono-spec">{t.email}</div>
      </div>
      <div className="flex items-center gap-2">
        <button className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5" onClick={onEdit} data-testid={`edit-teacher-${t.id}`}>
          <Pencil size={13} /> Modifier
        </button>
        {confirmDelete ? (
          <button className="!py-1.5 !px-3 text-xs bg-[var(--late)] text-white flex items-center gap-1.5" onClick={onConfirmDelete} data-testid={`confirm-delete-teacher-${t.id}`}>
            Confirmer ?
          </button>
        ) : (
          <button className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5 !text-[var(--late)]" onClick={onAskDelete} data-testid={`delete-teacher-${t.id}`}>
            <Trash2 size={13} /> Supprimer
          </button>
        )}
      </div>
    </div>
  );
}

export default function TeachersManager() {
  const { teachers, loading, dialog, setDialog, confirmId, setConfirmId, busy, save, remove } = useTeachersCrud();

  return (
    <div className="bg-white border border-[var(--parchment-2)] p-6" data-testid="teachers-manager">
      <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-[var(--parchment-2)]">
        <div>
          <div className="eyebrow">Comptes professeurs</div>
          <h2 className="font-serif-display text-2xl font-semibold mt-1">{teachers.length} professeur{teachers.length > 1 ? "s" : ""}</h2>
        </div>
        <button
          className="btn-primary !py-2 !px-4 text-sm flex items-center gap-2"
          onClick={() => setDialog({ mode: "create", name: "", email: "", password: "" })}
          data-testid="add-teacher-btn"
        >
          <Plus size={15} /> Nouveau professeur
        </button>
      </div>

      {loading ? (
        <div className="mt-6 text-[var(--ink-soft)]">Chargement...</div>
      ) : (
        <div className="mt-4 divide-y divide-[var(--parchment-2)]" data-testid="teachers-list">
          {teachers.map((t) => (
            <TeacherRow
              key={t.id}
              t={t}
              confirmDelete={confirmId === t.id}
              onEdit={() => setDialog({ mode: "edit", id: t.id, name: t.name, email: t.email, password: "", role: t.role })}
              onAskDelete={() => setConfirmId(t.id)}
              onConfirmDelete={() => remove(t.id)}
            />
          ))}
          {teachers.length === 0 && <div className="py-4 text-sm text-[var(--ink-soft)]">Aucun professeur.</div>}
        </div>
      )}

      <TeacherDialog dialog={dialog} setDialog={setDialog} busy={busy} onSave={save} onClose={() => setDialog(null)} />
    </div>
  );
}
